import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@mantine/core';
import { useDispatch } from 'react-redux';
import { ODataGrid } from '../../../../Components/OElements/ODataGrid';
import {
	getScheduledActivityListQuery,
	downloadScheduledActivityFile,
} from '../../../../slices/scheduledActivity/thunk';
import UploadActivityDocumentModal from '../Modals/UploadActivityDocumentModal';

const WorkScheduledActivitiesGrid = ({ workOrderId }) => {
	const dispatch = useDispatch();

	const [modalOpen, setModalOpen] = useState(false);
	const [selectedActivity, setSelectedActivity] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);

	const handleOpenUploadModal = activity => {
		setSelectedActivity(activity);
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedActivity(null);
	};

	const handleUploadSuccess = () => {
		setRefreshKey(k => k + 1);
	};

	const handleDownload = async activity => {
		try {
			// If file URL is already populated via queryPopulate, open it directly
			const fileUrl = activity?.file?.url;
			if (fileUrl) {
				window.open(fileUrl, '_blank', 'noopener,noreferrer');
				return;
			}
			// Fallback: call backend download endpoint to retrieve the URL
			const result = await dispatch(
				downloadScheduledActivityFile(activity._id)
			).unwrap();
			if (result?.url) {
				window.open(result.url, '_blank', 'noopener,noreferrer');
			}
		} catch (err) {
			console.error('Error al descargar documento:', err);
		}
	};
	// Helper function to format dates (DD/MM/YYYY)
	const formatDate = dateString => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '-';
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	};

	// Helper function to format datetime (DD/MM/YYYY HH:mm)
	const formatDateTime = dateString => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		if (isNaN(date.getTime())) return '-';
		const day = String(date.getDate()).padStart(2, '0');
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const year = date.getFullYear();
		const hours = String(date.getHours()).padStart(2, '0');
		const minutes = String(date.getMinutes()).padStart(2, '0');
		return `${day}/${month}/${year} ${hours}:${minutes}`;
	};

	// Status → Badge color mapping (matches activity_status.name values from backend)
	const getStatusColor = name => {
		if (!name) return 'gray';
		switch (name) {
			case 'Programado':
				return 'blue';
			case 'Reportado':
				return 'green';
			case 'Fuera de Plazo':
				return 'yellow';
			case 'Atrasado':
				return 'red';
			case 'En espera de resultados':
				return 'cyan';
			case 'Pendiente':
				return 'cyan';
			default:
				return 'gray';
		}
	};

	// Column definitions
	const columns = useMemo(
		() => [
			{
				header: 'Acción',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: ({ row }) => {
					const activityData = row.original;
					const canReport = !!activityData?.can_report;

					return (
						<div className='d-flex justify-content-center'>
							<div className='flex-shrink-0'>
								<ul className='list-inline mb-0'>
									<li className='list-inline-item'>
										<Link
											title={canReport ? 'Subir Documento' : 'No disponible para reportar'}
											to='#'
											className={canReport ? 'text-primary' : 'text-muted pe-none opacity-50'}
											onClick={e => {
												e.preventDefault();
												if (!canReport) return;
												handleOpenUploadModal(activityData);
											}}
											style={{ pointerEvents: canReport ? 'auto' : 'none' }}
										>
											<i className='ri-upload-cloud-2-line align-bottom me-1'></i>
										</Link>
									</li>
								</ul>
							</div>
						</div>
					);
				},
			},
		
			{
			header: 'Estado',
			dataType: 'select',
			accessorKey: 'activity_status.name',
			Cell: cellProps => {
				const statusName = cellProps.row.original.activity_status?.name;
				return (
					<Badge color={getStatusColor(statusName)} tt='capitalize'>
						{statusName || '-'}
					</Badge>
				);
			},
		},
		{
			header: 'Fecha de Reportabilidad',
			dataType: 'date',
			defaultSort: 'asc',
			accessorKey: 'reporting_date',
			Cell: cellProps => {
				return <span>{formatDate(cellProps.row.original.reporting_date)}</span>;
			},
		},
		{
			header: 'Periodo',
			dataType: 'string',
			accessorKey: 'period',
		},
		{
			header: 'Fecha Hora Reporte',
			dataType: 'datetime',
			accessorKey: 'report_datetime',
			Cell: cellProps => {
				return <span>{formatDateTime(cellProps.row.original.report_datetime)}</span>;
			},
		},
		{
			header: 'Usuario Reporte',
			dataType: 'string',
			accessorKey: 'modification_user_info.full_name',
		},
	],
	[]
);

	if (!workOrderId) return null;

	return (
		<>
		<ODataGrid
			columns={columns}
			getDataFunc={getScheduledActivityListQuery}
			baseFilters={{
				work_order_id: { $eq: workOrderId },
				disable: { $eq: false },
			}}
			queryPopulate={[
				'activity_status', 
				'file', 
				'work_order',
				'modification_user'
			]}
			remoteOperations={{ 
				pagination: true,
				sorting: true, 
				filtering: true 
			}}
			key={refreshKey}
			getRowIdTable={row => row._id}
			nameFile='Actividades Programadas'
		/>
		<UploadActivityDocumentModal
			isOpen={modalOpen}
			onClose={handleCloseModal}
			activity={selectedActivity}
			onSuccess={handleUploadSuccess}
		/>
		</>
	);
};

export default WorkScheduledActivitiesGrid;
